import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  User, Bell, Shield, Save, LogOut, 
  Smartphone, Send, Mail, Globe, 
  CheckCircle, XCircle, AlertCircle, Loader2,
  ExternalLink, Copy, RefreshCw, Lock, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  isPushSupported,
  getPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  sendTestNotification,
  isSafari
} from '../services/pushNotifications';

// Pro Feature Lock Component
const ProFeatureLock = ({ title }) => (
  <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg">
    <Lock className="h-8 w-8 text-amber-400 mb-2" />
    <p className="text-white font-medium mb-1">Pro Özelliği</p>
    <p className="text-slate-400 text-sm mb-3">{title} için Pro&apos;ya geç</p>
    <Link to="/payment?plan=pro&billing=yearly">
      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
        <Crown className="h-4 w-4 mr-1" />
        Pro&apos;ya Geç
      </Button>
    </Link>
  </div>
);

// Telegram Card Component
const TelegramCard = ({ isPro }) => {
  const [status, setStatus] = useState({ connected: false, loading: true });
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('idle'); // idle, connecting, verifying
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await axios.get('/telegram/status');
      setStatus({ ...response.data, loading: false });
    } catch (error) {
      setStatus({ connected: false, loading: false });
    }
  };

  const startConnect = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post('/telegram/start-connect');
      setVerificationCode(response.data.verification_code);
      setStep('connecting');
      toast.success('Doğrulama kodu oluşturuldu!');
    } catch (error) {
      toast.error('Bağlantı başlatılamadı');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyConnection = async () => {
    try {
      setIsLoading(true);
      await axios.post('/telegram/verify', { verification_code: verificationCode });
      toast.success('Telegram başarıyla bağlandı!');
      setStep('idle');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doğrulama başarısız');
    } finally {
      setIsLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      setIsLoading(true);
      await axios.delete('/telegram/disconnect');
      toast.success('Telegram bağlantısı kesildi');
      loadStatus();
    } catch (error) {
      toast.error('Bağlantı kesilemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      setIsLoading(true);
      await axios.post('/telegram/test');
      toast.success('Test mesajı gönderildi!');
    } catch (error) {
      toast.error('Test mesajı gönderilemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(verificationCode);
    toast.success('Kod kopyalandı!');
  };

  if (status.loading) {
    return (
      <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-white/10 bg-slate-900/50 backdrop-blur relative overflow-hidden ${status.connected ? 'border-blue-500/30' : ''}`}>
      {!isPro && <ProFeatureLock title="Telegram bildirimleri" />}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-400" />
            <CardTitle className="text-lg text-white">Telegram Bildirimleri</CardTitle>
          </div>
          {status.connected ? (
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              <CheckCircle className="h-3 w-3 mr-1" />
              Bağlı
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-400 border-slate-600">
              Bağlı Değil
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs text-slate-400">
          Safari ve iPhone kullanıcıları için önerilen bildirim kanalı
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {status.connected ? (
          // Connected State
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <p className="text-sm text-blue-300">
                ✅ @BorsamexSignalBot ile bağlantınız aktif
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={sendTest}
                variant="outline"
                size="sm"
                disabled={isLoading}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Test Mesajı
              </Button>
              <Button
                onClick={disconnect}
                variant="outline"
                size="sm"
                disabled={isLoading}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Bağlantıyı Kes
              </Button>
            </div>
          </div>
        ) : step === 'connecting' ? (
          // Connecting State
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-sm text-white mb-3">
                <strong>1.</strong> Telegram'da <a href="https://t.me/BorsamexSignalBot" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">@BorsamexSignalBot</a>'a gidin
              </p>
              <p className="text-sm text-white mb-3">
                <strong>2.</strong> Aşağıdaki kodu bot'a gönderin:
              </p>
              <div className="flex items-center gap-2 p-3 bg-slate-900 rounded-lg">
                <code className="text-2xl font-mono text-primary flex-1 text-center tracking-widest">
                  {verificationCode}
                </code>
                <Button variant="ghost" size="sm" onClick={copyCode}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-white mt-3">
                <strong>3.</strong> Bot'a kodu gönderdikten sonra aşağıdaki butona tıklayın
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={verifyConnection}
                size="sm"
                disabled={isLoading}
                className="btn-primary flex-1"
              >
                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Doğrula
              </Button>
              <Button
                onClick={() => setStep('idle')}
                variant="outline"
                size="sm"
                className="border-slate-600"
              >
                İptal
              </Button>
            </div>
            <a 
              href="https://t.me/BorsamexSignalBot" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-blue-400 hover:text-blue-300"
            >
              <ExternalLink className="h-4 w-4" />
              Telegram'da Bot'u Aç
            </a>
          </div>
        ) : (
          // Idle State
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-sm text-slate-300">
                Telegram ile bağlanarak trading sinyallerini anında alabilirsiniz.
                Safari ve iPhone'da en güvenilir bildirim yöntemidir.
              </p>
            </div>
            <Button
              onClick={startConnect}
              size="sm"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Telegram ile Bağlan
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is Pro
  const isPro = user?.is_pro || false;

  // Profile state
  const [profile, setProfile] = useState({
    name: user?.name || 'Kullanıcı',
    email: user?.email || 'user@example.com',
  });

  // Notification states
  const [notificationPrefs, setNotificationPrefs] = useState({
    push_enabled: false,
    pwa_enabled: false,
    telegram_enabled: false,
    telegram_chat_id: null,
    email_enabled: false,
    email_frequency: 'instant'
  });
  
  const [pushStatus, setPushStatus] = useState({
    supported: false,
    permission: 'default',
    subscribed: false,
    loading: true
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Load notification preferences and push status
  useEffect(() => {
    const loadNotificationStatus = async () => {
      try {
        // Check push support and status
        const supported = isPushSupported();
        console.log('Push supported:', supported);
        
        const permission = supported ? getPermissionStatus() : 'unsupported';
        console.log('Permission status:', permission);
        
        let subscribed = false;
        if (supported) {
          try {
            subscribed = await isSubscribedToPush();
            console.log('Is subscribed:', subscribed);
          } catch (e) {
            console.error('Error checking subscription:', e);
          }
        }

        setPushStatus({
          supported,
          permission,
          subscribed,
          loading: false
        });

        // Load preferences from backend
        try {
          const response = await axios.get('/notifications/preferences');
          setNotificationPrefs(response.data);
        } catch (e) {
          console.error('Error loading preferences:', e);
        }
      } catch (error) {
        console.error('Error loading notification status:', error);
        setPushStatus(prev => ({ ...prev, loading: false, supported: false }));
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(loadNotificationStatus, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleProfileSave = () => {
    toast.success('Profil bilgileri güncellendi!');
  };

  const handleEnablePush = async () => {
    try {
      setIsSaving(true);
      await subscribeToPush();
      
      setPushStatus(prev => ({
        ...prev,
        permission: 'granted',
        subscribed: true
      }));
      
      setNotificationPrefs(prev => ({ ...prev, push_enabled: true }));
      toast.success('Push bildirimleri aktif edildi!');
    } catch (error) {
      console.error('Push enable error:', error);
      if (error.message.includes('denied')) {
        toast.error('Bildirim izni reddedildi. Tarayıcı ayarlarından izin verin.');
      } else if (isSafari()) {
        toast.error('Safari push bildirimlerini tam desteklemiyor. Chrome veya Telegram kullanın.');
      } else {
        toast.error('Push bildirimleri aktif edilemedi: ' + error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisablePush = async () => {
    try {
      setIsSaving(true);
      await unsubscribeFromPush();
      
      setPushStatus(prev => ({ ...prev, subscribed: false }));
      setNotificationPrefs(prev => ({ ...prev, push_enabled: false }));
      toast.success('Push bildirimleri kapatıldı.');
    } catch (error) {
      toast.error('Push bildirimleri kapatılamadı.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      setIsTesting(true);
      await sendTestNotification();
      toast.success('Test bildirimi gönderildi!');
    } catch (error) {
      toast.error('Test bildirimi gönderilemedi.');
    } finally {
      setIsTesting(false);
    }
  };

  const handlePreferenceSave = async () => {
    try {
      setIsSaving(true);
      await axios.put('/notifications/preferences', notificationPrefs);
      toast.success('Bildirim tercihleri güncellendi!');
    } catch (error) {
      toast.error('Tercihler güncellenemedi.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Şifreler eşleşmiyor!');
      return;
    }
    toast.success('Şifre değiştirildi!');
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderPushStatus = () => {
    if (pushStatus.loading) {
      return (
        <Badge variant="outline" className="text-slate-400 border-slate-600">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Kontrol ediliyor...
        </Badge>
      );
    }
    
    if (!pushStatus.supported) {
      return (
        <Badge variant="outline" className="text-red-400 border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          Desteklenmiyor
        </Badge>
      );
    }
    
    if (pushStatus.permission === 'denied') {
      return (
        <Badge variant="outline" className="text-red-400 border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          İzin Reddedildi
        </Badge>
      );
    }
    
    if (pushStatus.subscribed) {
      return (
        <Badge className="bg-primary/20 text-primary border-primary/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Aktif
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="text-yellow-400 border-yellow-500/30">
        <AlertCircle className="h-3 w-3 mr-1" />
        Kapalı
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-brand-dark py-6">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-white mb-1">Ayarlar</h1>
          <p className="text-sm text-slate-400">Hesap ve bildirim tercihlerini düzenle.</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="notifications" className="w-full">
          <TabsList className="bg-slate-900/50 border border-white/10 mb-5">
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
              <User className="h-4 w-4 mr-2" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
              <Bell className="h-4 w-4 mr-2" />
              Bildirimler
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
              <Shield className="h-4 w-4 mr-2" />
              Güvenlik
            </TabsTrigger>
          </TabsList>

          {/* Profil Tab */}
          <TabsContent value="profile">
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Profil Bilgileri</CardTitle>
                <CardDescription className="text-xs text-slate-400">Hesap bilgilerini güncelle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm text-white">Ad Soyad</Label>
                  <Input
                    id="name"
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="bg-slate-800/50 border-white/10 text-white h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm text-white">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    disabled
                    className="bg-slate-800/30 border-white/10 text-slate-400 cursor-not-allowed h-9"
                  />
                  <p className="text-xs text-slate-500">Email adresi değiştirilemez</p>
                </div>
                <Button onClick={handleProfileSave} className="btn-primary w-full md:w-auto" size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bildirimler Tab */}
          <TabsContent value="notifications" className="space-y-4">
            {/* Browser Notifications - Always Active */}
            <Card className="border-primary/30 bg-primary/5 backdrop-blur">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg text-white">Tarayıcı Bildirimleri</CardTitle>
                  </div>
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Her Zaman Aktif
                  </Badge>
                </div>
                <CardDescription className="text-xs text-slate-400">
                  Site açıkken sinyal bildirimleri otomatik olarak gösterilir
                </CardDescription>
              </CardHeader>
            </Card>

            {/* PWA / Push Notifications */}
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur relative overflow-hidden">
              {!isPro && <ProFeatureLock title="Push bildirimleri" />}
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-blue-400" />
                    <CardTitle className="text-lg text-white">Push Bildirimleri (PWA)</CardTitle>
                  </div>
                  {renderPushStatus()}
                </div>
                <CardDescription className="text-xs text-slate-400">
                  Tarayıcı kapalıyken bile bildirim al. Mobilde uygulama gibi çalışır.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {pushStatus.supported ? (
                  <>
                    {pushStatus.permission === 'denied' ? (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                        <p className="text-sm text-red-400">
                          Bildirim izni reddedilmiş. Tarayıcı ayarlarından izni değiştirin.
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2">
                        {pushStatus.subscribed ? (
                          <>
                            <Button
                              onClick={handleDisablePush}
                              variant="outline"
                              size="sm"
                              disabled={isSaving}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              {isSaving ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <XCircle className="h-4 w-4 mr-2" />
                              )}
                              Bildirimleri Kapat
                            </Button>
                            <Button
                              onClick={handleTestNotification}
                              variant="outline"
                              size="sm"
                              disabled={isTesting}
                              className="border-primary/30 text-primary hover:bg-primary/10"
                            >
                              {isTesting ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4 mr-2" />
                              )}
                              Test Bildirimi Gönder
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={handleEnablePush}
                            size="sm"
                            disabled={isSaving}
                            className="btn-primary"
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Bell className="h-4 w-4 mr-2" />
                            )}
                            Bildirimleri Aktif Et
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* PWA Install Hint */}
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <p className="text-sm text-blue-300">
                        💡 <strong>İpucu:</strong> Mobilde "Ana Ekrana Ekle" yaparak uygulama gibi kullanabilirsiniz.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <p className="text-sm text-slate-400">
                      {isSafari() ? (
                        <>
                          <strong className="text-yellow-400">Safari'de push bildirimleri sınırlı desteklenir.</strong>
                          <br />
                          Chrome veya Edge tarayıcı kullanmanızı veya Telegram bildirimlerini tercih etmenizi öneririz.
                        </>
                      ) : (
                        'Tarayıcınız push bildirimlerini desteklemiyor. Modern bir tarayıcı kullanmayı deneyin.'
                      )}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Telegram */}
            <TelegramCard isPro={isPro} />

            {/* Email */}
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur relative overflow-hidden">
              {!isPro && <ProFeatureLock title="Email bildirimleri" />}
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-purple-400" />
                    <CardTitle className="text-lg text-white">Email Bildirimleri</CardTitle>
                  </div>
                  <Switch
                    checked={notificationPrefs.email_enabled}
                    onCheckedChange={(checked) => 
                      setNotificationPrefs(prev => ({ ...prev, email_enabled: checked }))
                    }
                  />
                </div>
                <CardDescription className="text-xs text-slate-400">
                  Sinyal özetlerini email olarak al
                </CardDescription>
              </CardHeader>
              {notificationPrefs.email_enabled && (
                <CardContent className="pt-0 space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm text-white">Email Sıklığı</Label>
                    <div className="flex gap-2">
                      {[
                        { value: 'instant', label: 'Anlık' },
                        { value: 'daily', label: 'Günlük Özet' },
                        { value: 'weekly', label: 'Haftalık Özet' }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          variant="outline"
                          size="sm"
                          onClick={() => setNotificationPrefs(prev => ({ 
                            ...prev, 
                            email_frequency: option.value 
                          }))}
                          className={
                            notificationPrefs.email_frequency === option.value
                              ? 'bg-primary/20 border-primary text-primary'
                              : 'border-slate-600 text-slate-400'
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Save Button */}
            <Button 
              onClick={handlePreferenceSave} 
              className="btn-primary w-full" 
              size="sm"
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Tercihleri Kaydet
            </Button>
          </TabsContent>

          {/* Güvenlik Tab */}
          <TabsContent value="security">
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Şifre Değiştir</CardTitle>
                <CardDescription className="text-xs text-slate-400">Hesap güvenliğin için güçlü bir şifre kullan</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="space-y-1.5">
                  <Label htmlFor="currentPassword" className="text-sm text-white">Mevcut Şifre</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="bg-slate-800/50 border-white/10 text-white h-9"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword" className="text-sm text-white">Yeni Şifre</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="bg-slate-800/50 border-white/10 text-white h-9"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-sm text-white">Yeni Şifre (Tekrar)</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="bg-slate-800/50 border-white/10 text-white h-9"
                    placeholder="••••••••"
                  />
                </div>
                <Button onClick={handlePasswordChange} className="btn-primary w-full md:w-auto" size="sm">
                  <Shield className="h-4 w-4 mr-2" />
                  Şifreyi Değiştir
                </Button>
              </CardContent>
            </Card>

            {/* Oturum Yönetimi */}
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Oturum Yönetimi</CardTitle>
                <CardDescription className="text-xs text-slate-400">Hesabından çıkış yap</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Button onClick={handleLogout} variant="destructive" className="w-full md:w-auto" size="sm">
                  <LogOut className="h-4 w-4 mr-2" />
                  Oturumu Kapat
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
